import type { Tour } from '../tour-types';
import { canUseServerSide } from 'definitions/server-side-access';

/**
 * The Settings tab of an experiment, in the order it reads down the page. Only a Code
 * experiment (stored type 'Server Side') has the Assignment card, and one whose server
 * assigns visitors has no targeting card, since its page rules never run; only a draft or a
 * running experiment has a schedule. Those steps drop out elsewhere. The variant buttons only
 * render while they can be used (Edit Weights on a draft or running test, Add New Variant on
 * a draft).
 *
 * The Start button is the one header element here: setting up is what this tab leads to.
 * It is rendered in both the desktop and the mobile row; the step finds whichever is shown.
 */
export const experimentSettingsTour: Tour = {
  key: 'experiments.settings',
  version: 3,
  routes: ['/experiments/:id'],
  screen: 'settings',
  steps: [
    {
      anchor: 'settings-variants',
      placement: 'bottom',
      title: 'Variants',
      body: 'Every version of the page a visitor can be shown, with its share of traffic and how many changes it makes. The control is the one the others are measured against.',
    },
    {
      anchor: 'settings-variants-actions',
      placement: 'left',
      requires: 'experiments:create',
      title: 'Split and add',
      body: 'Edit Weights changes how traffic is split, on a draft or while the test runs. Add New Variant is only offered while the experiment is a draft.',
    },
    {
      anchor: 'settings-variant-menu',
      placement: 'left',
      requires: 'experiments:create',
      title: 'Variant options',
      body: 'Rename a variant, make it the control, or delete it. Deleting is for drafts only, and an experiment always keeps two variants.',
    },
    {
      anchor: 'settings-objectives',
      placement: 'bottom',
      title: 'Objectives',
      body: 'The primary objective decides the result; with none picked, the website default is used. Secondary objectives are reported alongside it as supporting evidence and never change the verdict.',
    },
    {
      anchor: 'settings-server-side',
      placement: 'top',
      since: 2,
      // Shown to whoever may use server-side (PER-178's switch; open to every account now).
      when: canUseServerSide,
      title: 'Assignment',
      body: 'Choose whether the Pertento snippet or your own server, through the SDK, decides which variant each visitor gets. For the server, set the flag key, the ID to bucket on and any attribute rules, then copy the code.',
    },
    {
      anchor: 'settings-server-side-guides',
      placement: 'top',
      since: 3,
      when: canUseServerSide,
      title: 'More guides',
      body: 'Guides on reporting conversions from your server, attribute rules, the HTTP API and visitors not being counted. They open in a new tab.',
    },
    {
      anchor: 'settings-targeting-cookie',
      placement: 'right',
      title: 'Cookie targeting',
      body: 'Shows the experiment only to visitors whose cookies contain one of the values you list. Drag it into the target area to set the values.',
    },
    {
      anchor: 'settings-targeting-url',
      placement: 'right',
      title: 'URL targeting',
      body: 'Limits the experiment to pages whose address matches a rule, such as equals, starts with or contains. With no URL rule it runs on every page.',
    },
    {
      anchor: 'settings-targeting-device',
      placement: 'right',
      title: 'Device targeting',
      body: 'Limits the experiment to desktop, tablet or mobile visitors. With no device rule it runs on all of them.',
    },
    {
      anchor: 'settings-targeting-activation',
      placement: 'right',
      title: 'Activation',
      body: 'Holds the experiment back until the site sends an event or an element appears on the page. Every activation rule has to be met before it runs.',
    },
    {
      anchor: 'settings-targeting-area',
      placement: 'left',
      title: 'Target area',
      body: 'Drop conditions here to make them rules. A visitor must pass every section that has rules, and inside URL, cookie and device one matching rule is enough.',
    },
    {
      anchor: 'settings-schedule',
      placement: 'top',
      title: 'Schedule',
      body: 'Start and stop the experiment at set times, or let it stop early once the result is conclusive. Stopping early needs at least two weeks of traffic, so a stop time is kept as a backstop.',
    },
    {
      anchor: 'settings-activity',
      placement: 'top',
      title: 'Activity log',
      body: 'Who created, started, reweighted, deployed or ended the experiment, and when. Check it when a result looks odd.',
    },
    {
      anchor: 'experiment-start',
      placement: 'left',
      requires: 'experiments:start',
      title: 'Start when ready',
      body: 'Start QA previews the setup on the live site through a link, never shown to visitors; Start experiment puts it in front of visitors. While it runs, targeting changes reach visitors once you press Enforce new Rules.',
    },
  ],
};
